import { useCallback, useEffect, useMemo, useState } from 'react';
import CodigoQR from '../ui/CodigoQR';
import {
  ShoppingCart, Search, Plus, Minus, Trash2, Store, MapPin, Loader2,
  ArrowLeft, PackageX,
} from 'lucide-react';
import { tienda, ErrorTienda } from './api';
import { useCapa, useSubpantalla } from '../ui/navegacionReact';
import * as carrito from './carrito';

// EL ESCAPARATE PÚBLICO (PRD §5.F).
//
// LA PRIMERA PANTALLA DE TEGO QUE VE ALGUIEN SIN CUENTA. No hay menú que
// aprender, ni sesión, ni nada que configurar: se llega desde un QR pegado
// en el mostrador, se arma un pedido y se enseña un código en la caja.
//
// TRES COSAS QUE LA GOBIERNAN:
//
//  1. NO SE PROMETE LO QUE NO SE PUEDE CUMPLIR. Solo aparece lo que hay en
//     ESA sucursal ahora. Y como el pedido no aparta nada —decisión del
//     dueño: un anónimo no puede congelar inventario armando carritos—, se
//     dice con todas las letras que hay que ir pronto.
//  2. EL CARRITO SOBREVIVE. Se guarda en el navegador por tienda y
//     sucursal: una llamada entrante no puede borrar diez minutos de
//     trabajo.
//  3. EL CÓDIGO SE PUEDE VOLVER A VER. Si se cierra la pestaña con el QR
//     en la mano, se recupera con el folio; sin eso, el pedido existe en
//     el sistema y no en el teléfono de quien lo hizo.

const ULTIMO_PEDIDO = 'tego_tienda_ultimo_pedido';
const SUCURSAL_ELEGIDA = 'tego_tienda_sucursal';

function claveDeLaRuta() {
  const partes = window.location.pathname.split('/').filter(Boolean);
  return partes[0] === 'tienda' ? decodeURIComponent(partes[1] || '') : '';
}

function guardarLocal(k, v) {
  try { localStorage.setItem(k, v); } catch { /* modo privado */ }
}
function leerLocal(k) {
  try { return localStorage.getItem(k); } catch { return null; }
}

const pesos = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

export default function TiendaApp() {
  const clave = useMemo(() => claveDeLaRuta(), []);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [sucursal, setSucursal] = useState(null);
  const [pedido, setPedido] = useState(null);
  // El ticket del pedido es una subpantalla (TC-DR-AT): atrás hace lo mismo
  // que «Armar otro pedido». Desde el catálogo, atrás sale de la tienda.
  useSubpantalla('pedido', !!pedido, () => { setPedido(null); guardarLocal(ULTIMO_PEDIDO, ''); });

  useEffect(() => {
    // Sin clave no se pregunta nada. El aviso NO se pone aquí con
    // `setError`: cambiar estado dentro de un efecto encadena renders y el
    // lint lo señala con razón. Es un dato que ya se sabe al pintar, así
    // que se decide abajo, sin estado de por medio.
    if (!clave) return undefined;
    let vivo = true;
    tienda.ver(clave)
      .then((d) => {
        if (!vivo) return;
        setDatos(d);
        // CON UNA SOLA SUCURSAL NO SE PREGUNTA. Obligar a elegir entre una
        // única opción es trabajo que la pantalla puede ahorrarse.
        const guardada = leerLocal(SUCURSAL_ELEGIDA);
        const existe = d.sucursales.find((s) => String(s.id) === guardada);
        if (d.sucursales.length === 1) setSucursal(d.sucursales[0]);
        else if (existe) setSucursal(existe);
      })
      .catch((e) => vivo && setError(e instanceof ErrorTienda ? e.message
        : 'No se pudo abrir la tienda.'));
    return () => { vivo = false; };
  }, [clave]);

  // Un pedido reciente se recupera solo: quien cerró la pestaña con el QR
  // en la mano no tiene por qué volver a armarlo.
  useEffect(() => {
    const folio = leerLocal(ULTIMO_PEDIDO);
    if (!folio || !clave) return;
    tienda.verPedido(clave, folio)
      .then((p) => { if (p.estado === 'PENDIENTE') setPedido(p); })
      .catch(() => {});
  }, [clave]);

  if (!clave) {
    return (
      <Aviso titulo="Falta el código de la tienda"
             texto="La dirección está incompleta. Vuelve a escanear el código del
                    mostrador, o pídeles el enlace." />
    );
  }
  if (error) return <Aviso titulo="No se pudo abrir la tienda" texto={error} />;
  if (!datos) return <Cargando />;

  if (pedido) {
    return (
      <Marco nombre={datos.nombre}>
        <Ticket pedido={pedido} onVolver={() => { setPedido(null); guardarLocal(ULTIMO_PEDIDO, ''); }} />
      </Marco>
    );
  }

  if (!sucursal) {
    return (
      <Marco nombre={datos.nombre}>
        <ElegirSucursal
          sucursales={datos.sucursales}
          onElegir={(s) => { setSucursal(s); guardarLocal(SUCURSAL_ELEGIDA, String(s.id)); }}
        />
      </Marco>
    );
  }

  return (
    <Marco nombre={datos.nombre} sucursal={sucursal}
           onCambiarSucursal={datos.sucursales.length > 1 ? () => setSucursal(null) : null}>
      {/* `key` con la sucursal: cambiarla REMONTA el catálogo, así que el
          carrito se relee de cero sin un efecto que sincronice estado con
          estado. Un efecto ahí encadena renders y es justo donde aparecen
          los parpadeos raros. */}
      <Catalogo key={sucursal.id} clave={clave} tiendaDatos={datos} sucursal={sucursal}
                onPedido={(p) => { setPedido(p); guardarLocal(ULTIMO_PEDIDO, p.folio); }} />
    </Marco>
  );
}

function Marco({ nombre, sucursal, onCambiarSucursal, children }) {
  return (
    <div className="min-h-dvh bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center gap-2">
            <Store className="h-5 w-5 text-gray-500" aria-hidden="true" />
            <h1 className="font-semibold text-gray-900 truncate">{nombre}</h1>
          </div>
          {/* SE EXPLICA SOLA. Quien llega aquí desde un QR no sabe qué es
              esto ni qué se espera de él. */}
          <p className="text-xs text-gray-600 mt-0.5">
            Arma tu pedido y enséñalo en el mostrador para pagarlo.
          </p>
          {sucursal && (
            <div className="flex items-center gap-1.5 text-xs text-gray-700 mt-1.5">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              Recoges en <strong>{sucursal.nombre}</strong>
              {onCambiarSucursal && (
                <button type="button" onClick={onCambiarSucursal}
                        className="underline font-semibold text-[var(--color-marca,#0f766e)]">
                  cambiar
                </button>
              )}
            </div>
          )}
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-4">{children}</main>
    </div>
  );
}

function Cargando() {
  return (
    <div className="min-h-dvh flex items-center justify-center text-gray-500 gap-2">
      <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Abriendo la tienda…
    </div>
  );
}

function Aviso({ titulo, texto }) {
  return (
    <div className="min-h-dvh flex items-center justify-center p-6">
      <div className="text-center max-w-sm space-y-2">
        <PackageX className="h-10 w-10 text-gray-400 mx-auto" aria-hidden="true" />
        <h1 className="font-semibold text-gray-900">{titulo}</h1>
        <p className="text-sm text-gray-600">{texto}</p>
      </div>
    </div>
  );
}

function ElegirSucursal({ sucursales, onElegir }) {
  return (
    <div className="space-y-3">
      <div>
        <h2 className="font-semibold text-gray-900">¿Dónde vas a recoger?</h2>
        <p className="text-sm text-gray-600">
          Lo que te enseñemos depende de la sucursal: solo verás lo que hay
          disponible ahí.
        </p>
      </div>
      {sucursales.map((s) => (
        <button key={s.id} type="button" onClick={() => onElegir(s)}
                className="w-full text-left bg-white border border-gray-200 rounded-xl
                           px-4 py-4 font-medium text-gray-900 flex items-center gap-2">
          <MapPin className="h-4 w-4 text-gray-400" aria-hidden="true" /> {s.nombre}
        </button>
      ))}
    </div>
  );
}

function Catalogo({ clave, tiendaDatos, sucursal, onPedido }) {
  const [productos, setProductos] = useState(null);
  const [buscar, setBuscar] = useState('');
  const [renglones, setRenglones] = useState(() => carrito.leer(clave, sucursal.id));
  const [abierto, setAbierto] = useState(false);
  useCapa(abierto, () => setAbierto(false));       // la hoja «Tu pedido»: atrás la cierra
  const [error, setError] = useState(null);

  const cargar = useCallback(() => {
    setProductos(null);
    tienda.productos(clave, { sucursal: sucursal.id, buscar })
      .then(setProductos)
      .catch((e) => { setProductos([]); setError(e.message); });
  }, [clave, sucursal.id, buscar]);

  useEffect(() => {
    const t = setTimeout(cargar, buscar ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, buscar]);

  const total = carrito.cuantos(renglones);

  return (
    <div className="space-y-3 pb-24">
      <label className="sr-only" htmlFor="buscar">Buscar</label>
      <div className="relative">
        <Search className="h-4 w-4 text-gray-400 absolute left-3 top-3.5" aria-hidden="true" />
        <input id="buscar" value={buscar} onChange={(e) => setBuscar(e.target.value)}
               placeholder="Buscar en la tienda"
               className="w-full border border-gray-300 rounded-xl pl-9 pr-4 py-3 text-base" />
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 text-red-800 rounded-xl p-3 text-sm">
          {error}
        </div>
      )}

      {productos === null && (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Cargando…
        </div>
      )}

      {productos?.length === 0 && !error && (
        <div className="text-center py-12 text-gray-500 space-y-1">
          <PackageX className="h-8 w-8 mx-auto text-gray-400" aria-hidden="true" />
          <p className="text-sm">
            {buscar
              ? `No hay nada que se llame «${buscar}» disponible en ${sucursal.nombre}.`
              : `Ahora mismo no hay nada disponible en ${sucursal.nombre}.`}
          </p>
          <p className="text-xs">Pregunta en el mostrador: puede que sí lo tengan.</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {productos?.map((p) => (
          <Producto key={p.sku} producto={p}
                    onAgregar={() => setRenglones(carrito.agregar(clave, sucursal.id, p))} />
        ))}
      </div>

      {total > 0 && !abierto && (
        <button type="button" onClick={() => setAbierto(true)}
                className="fixed bottom-4 left-4 right-4 max-w-3xl mx-auto
                           bg-[var(--color-marca,#0f766e)] text-white font-semibold
                           rounded-xl py-4 flex items-center justify-center gap-2 shadow-lg">
          <ShoppingCart className="h-5 w-5" aria-hidden="true" />
          Ver mi pedido ({total})
        </button>
      )}

      {abierto && (
        <Carrito clave={clave} tiendaDatos={tiendaDatos} sucursal={sucursal}
                 renglones={renglones} setRenglones={setRenglones}
                 onCerrar={() => setAbierto(false)} onPedido={onPedido} />
      )}
    </div>
  );
}

function Producto({ producto, onAgregar }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 flex gap-3">
      {producto.imagen
        ? <img src={producto.imagen} alt="" className="h-16 w-16 object-cover rounded-lg shrink-0" />
        : <div className="h-16 w-16 rounded-lg bg-gray-100 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 text-sm">{producto.nombre}</div>
        {producto.descripcion && (
          <div className="text-xs text-gray-500 line-clamp-2">{producto.descripcion}</div>
        )}
        <div className="flex items-center justify-between mt-1.5 gap-2">
          {/* El precio puede no venir: la empresa puede apagarlo. Cuando no
              está, no se inventa un «consultar» que parezca un precio. */}
          <span className="font-semibold text-gray-900 text-sm">
            {producto.precio != null ? pesos.format(producto.precio) : ''}
          </span>
          <button type="button" onClick={onAgregar}
                  aria-label={`Agregar ${producto.nombre}`}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-white
                             bg-[var(--color-marca,#0f766e)] px-3 py-2 rounded-lg">
            <Plus className="h-4 w-4" aria-hidden="true" /> Agregar
          </button>
        </div>
      </div>
    </div>
  );
}

function Carrito({ clave, tiendaDatos, sucursal, renglones, setRenglones,
                   onCerrar, onPedido }) {
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  async function confirmar() {
    setEnviando(true);
    setError(null);
    try {
      const p = await tienda.crearPedido(clave, {
        id_sucursal: sucursal.id,
        renglones: renglones.map((r) => ({ sku: r.sku, cantidad: r.cantidad })),
        nombre_cliente: nombre.trim() || null,
        telefono_cliente: telefono.trim() || null,
      });
      carrito.vaciar(clave, sucursal.id);
      onPedido(p);
    } catch (e) {
      setError(e.message);
      setEnviando(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-20">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-4
                      max-h-[90vh] overflow-y-auto space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Tu pedido</h2>
          <button type="button" onClick={onCerrar}
                  className="text-sm font-semibold text-gray-600 inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Seguir viendo
          </button>
        </div>

        {renglones.map((r) => (
          <div key={r.sku} className="flex items-center gap-2 border-b border-gray-100 pb-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-900 truncate">{r.nombre}</div>
            </div>
            <button type="button" aria-label={`Quitar uno de ${r.nombre}`}
                    onClick={() => setRenglones(carrito.fijar(clave, sucursal.id, r.sku, r.cantidad - 1))}
                    className="h-9 w-9 rounded-lg border border-gray-300 flex items-center justify-center">
              <Minus className="h-4 w-4" aria-hidden="true" />
            </button>
            <span className="w-6 text-center tabular-nums text-sm">{r.cantidad}</span>
            <button type="button" aria-label={`Agregar otro de ${r.nombre}`}
                    onClick={() => setRenglones(carrito.fijar(clave, sucursal.id, r.sku, r.cantidad + 1))}
                    className="h-9 w-9 rounded-lg border border-gray-300 flex items-center justify-center">
              <Plus className="h-4 w-4" aria-hidden="true" />
            </button>
            <button type="button" aria-label={`Quitar ${r.nombre} del pedido`}
                    onClick={() => setRenglones(carrito.quitar(clave, sucursal.id, r.sku))}
                    className="h-9 w-9 rounded-lg text-gray-400 flex items-center justify-center">
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        ))}

        {/* Decisión del dueño: el pedido NO aparta stock. Se dice aquí, que
            es donde alguien está a punto de contar con ello, y no en letra
            pequeña después. */}
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
          Esto no aparta la mercancía: se cobra en el mostrador y hasta
          entonces cualquiera puede llevársela. Tienes {tiendaDatos.horas_vigencia} horas.
        </p>

        <div className="space-y-2">
          <label className="block text-xs text-gray-600" htmlFor="nombre">
            Tu nombre <span className="text-gray-400">(opcional, para llamarte por él)</span>
          </label>
          <input id="nombre" value={nombre} onChange={(e) => setNombre(e.target.value)}
                 maxLength={160}
                 className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-base" />
          <label className="block text-xs text-gray-600" htmlFor="telefono">
            Teléfono <span className="text-gray-400">(opcional, por si hay que avisarte)</span>
          </label>
          <input id="telefono" value={telefono} inputMode="tel" maxLength={40}
                 onChange={(e) => setTelefono(e.target.value)}
                 className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-base" />
        </div>

        {error && (
          <div className="border border-red-200 bg-red-50 text-red-800 rounded-xl p-3 text-sm">
            {error}
          </div>
        )}

        <button type="button" onClick={confirmar}
                disabled={enviando || renglones.length === 0}
                className="w-full bg-[var(--color-marca,#0f766e)] text-white font-semibold
                           rounded-xl py-4 disabled:opacity-50 inline-flex items-center
                           justify-center gap-2">
          {enviando
            ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            : <ShoppingCart className="h-5 w-5" aria-hidden="true" />}
          {enviando ? 'Generando tu código…' : 'Generar mi código'}
        </button>
      </div>
    </div>
  );
}

function Ticket({ pedido, onVolver }) {
  return (
    <div className="space-y-4 text-center">
      <div>
        <h2 className="font-semibold text-gray-900 text-lg">Tu pedido está listo</h2>
        <p className="text-sm text-gray-600">
          Enseña este código en el mostrador para pagarlo.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-6 inline-block mx-auto">
        <CodigoQR valor={pedido.folio} tamano={220}
                  etiqueta={`Código QR del pedido ${pedido.folio}`} />
        {/* EL FOLIO EN TEXTO, DEBAJO DEL QR Y GRANDE. La cámara del
            mostrador no siempre enfoca —pantalla rayada, poca luz, brillo
            bajo— y entonces alguien lo teclea. Sin esto, el pedido existe
            y no hay forma de cobrarlo. */}
        <div className="mt-3 font-mono text-xl font-bold tracking-wider text-gray-900">
          {pedido.folio}
        </div>
      </div>

      <div className="text-left bg-white border border-gray-200 rounded-xl p-3 space-y-1">
        {(pedido.renglones || []).map((r) => (
          <div key={r.sku} className="flex justify-between text-sm">
            <span className="text-gray-700">{r.cantidad} × {r.nombre_visto}</span>
            {r.precio_visto != null && (
              <span className="text-gray-900">{pesos.format(r.precio_visto * r.cantidad)}</span>
            )}
          </div>
        ))}
        {pedido.total_estimado > 0 && (
          <div className="flex justify-between text-sm font-semibold pt-1 border-t border-gray-100">
            <span>Aproximado</span><span>{pesos.format(pedido.total_estimado)}</span>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500">
        El total lo confirma el mostrador. Si algo cambió de precio o ya no
        queda, te lo dirán ahí.
      </p>

      <button type="button" onClick={onVolver}
              className="text-sm font-semibold text-[var(--color-marca,#0f766e)] underline">
        Armar otro pedido
      </button>
    </div>
  );
}
